<!--チケットの販売日カレンダー-->
<template>
  <div v-if="isReadyToDraw" class="calendar">
    <!-- 販売日カレンダー -->
    <v-calendar
      class="custom-calendar"
      :masks="masks"
      :attributes="calendarAttributes"
      disable-page-swipe
      is-expanded
    >
      <template #day-content="{ day, attributes }">
        <div
          class="cell-content"
          :class="[checkAttributes(attributes) ? 'calendar-disabled' : '']"
          :style="{ '--price-count': maxPriceCount }"
          @click="setDate(day, attributes)"
        >
          <span
            :class="[
              day.id == propStartDateOfUse ? 'selected-day-label' : 'day-label',
              isPriceCalendar ? '' : 'closed-calendar',
            ]"
            >{{ day.day }}
          </span>
          <div v-if="isPriceCalendar" class="price-area">
            <div v-for="attr in attributes" :key="attr.key">
              <p v-for="price in attr.customData.prices" :key="price.id">
                ¥{{ price.price.toLocaleString() }}
              </p>
            </div>
          </div>
        </div>
      </template>
    </v-calendar>
  </div>
</template>

<script>
import commonService from "@/services/common";
import dateService from "@/services/bookBookingDate";
import moment from "@/services/moment";
import { computed, ref, onMounted } from "@vue/runtime-core";

export default {
  name: "TheCalendar",
  props: {
    propMasterBook: {
      type: Object,
      required: true,
    },
    propStartDateOfUse: {
      type: String,
      required: true,
    },
    propOnSelectedDate: {
      type: Function,
      required: true,
    },
  },

  setup(props) {
    const masks = {
      weekdays: "WWW",
    };

    const calendarAttributes = ref([]);
    const maxPriceCount = ref(0);

    const isReadyToDraw = ref(false);
    const isPriceCalendar = computed(() =>
      dateService.checkIsPriceCalendar(props.propMasterBook)
    );
    const isClosedCalendar = computed(() =>
      dateService.checkIsClosedCalendar(props.propMasterBook)
    );

    const setDate = (day, attributes) => {
      // 料金カレンダーの場合は、価格設定のある日付かチェックする
      if (!checkAttributes(attributes)) {
        props.propOnSelectedDate(day.id);
      }
    };

    // カレンダーUIで表示させる販売カレンダーの情報を生成する。
    const setCalenderAttributes = () => {
      // 販売期間をpurchase_started_atとpurchase_limited_atから設定する。
      const now = moment(new Date(props.propMasterBook.purchase_started_at));
      const monthLater = moment(
        new Date(props.propMasterBook.purchase_limited_at)
      );

      // 料金カレンダー(料金詳細あり、休業日設定あれば除外)
      if (isPriceCalendar.value) {
        const calendars = props.propMasterBook.price_calendars;
        maxPriceCount.value =
          calendars.length > 0
            ? Math.max(
                ...calendars.map(
                  (calendar) => calendar.master_prices?.length || 0
                )
              )
            : 0;

        let priceCalendars = calendars;

        if (isClosedCalendar.value) {
          priceCalendars = priceCalendars.filter((calendar) => {
            return !props.propMasterBook.closed_days.find(
              (closed_day) => closed_day.closed_date === calendar.start_date
            );
          });
        }

        calendarAttributes.value = priceCalendars.map((calendar) => {
          return {
            key: calendar.start_date,
            customData: {
              prices: commonService.sortByDisplayOrder(calendar.master_prices),
            },
            dates: new Date(calendar.start_date),
          };
        });
        // 休業日除外カレンダー(料金詳細なし、休業日設定あり)
      } else if (isClosedCalendar.value) {
        // closed_daysに含まれない日付を販売カレンダーの情報としてセットする。
        for (let d = now; now <= monthLater; d.add(1, "d")) {
          if (
            !props.propMasterBook.closed_days.find(
              (closed_day) => closed_day.closed_date == now.format("YYYY-MM-DD")
            )
          ) {
            calendarAttributes.value.push({
              key: now.format("YYYY-MM-DD"),
              customData: {}, //使用しないが他に合わせて項目だけ用意する。
              dates: new Date(now.format("YYYY-MM-DD")),
            });
          }
        }
        // 通常チケット販売カレンダー
      } else {
        // 販売期間をカレンダーの情報としてセットする。
        for (let d = now; now <= monthLater; d.add(1, "d")) {
          calendarAttributes.value.push({
            key: now.format("YYYY-MM-DD"),
            customData: {}, //使用しないが他に合わせて項目だけ用意する。
            dates: new Date(now.format("YYYY-MM-DD")),
          });
        }
      }
    };

    const checkAttributes = (attribute) => {
      return attribute == null || attribute.length === 0;
    };

    onMounted(() => {
      setCalenderAttributes();
      isReadyToDraw.value = true;
    });

    return {
      masks,
      calendarAttributes,
      isPriceCalendar,
      isClosedCalendar,
      isReadyToDraw,
      setDate,
      checkAttributes,
      maxPriceCount,
    };
  },
};
</script>

<style lang="scss" scoped>
.calendar {
  padding-bottom: 3%;
}
:deep(.custom-calendar.vc-container) {
  --day-border: 1px solid #54575a;
  --day-border-highlight: 1px solid #b8c2cc;
  --day-width: 70px;
  --day-height: 70px;
  --weekday-bg: #f8fafc;
  --weekday-border: 1px solid #eaeaea;
  border-radius: 0;
  max-width: 100%;
  & .vc-header {
    background-color: #f1f5f8;
    padding: 10px 0;
  }
  & .vc-weeks {
    padding: 0;
  }
  & .vc-weekday {
    background-color: var(--weekday-bg);
    border-bottom: var(--weekday-border);
    border-top: var(--weekday-border);
    padding: 5px 0;
  }
  & .vc-day {
    text-align: center;
    background-color: white;
    border-right: var(--day-border-highlight);
    border-bottom: var(--day-border-highlight);
  }
  & .vc-day-dots {
    margin-bottom: 5px;
  }
}
.cell-content {
  --day-label-height: 24px;
  --price-row-height: 16px;
  --cell-padding: 8px;
  --cell-min-height: 65px;
  height: max(
    var(--cell-min-height),
    calc(
      var(--day-label-height) + var(--price-count, 0) * var(--price-row-height) +
        var(--cell-padding)
    )
  );
  flex-direction: column;
  z-index: 10;
  overflow: hidden;
}
.day-label {
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 0.875rem;
  color: #1a202c;
  width: 24px;
  height: 24px;
  line-height: 24px;
  border-radius: 9999px;
}
.selected-day-label {
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 0.875rem;
  color: #ffffff;
  background-color: #ffb206;
  width: 24px;
  height: 24px;
  line-height: 24px;
  border-radius: 9999px;
}
.price-area {
  flex-grow: 1;
  p {
    font-size: 0.7rem;
    line-height: 1.25;
    border-radius: 0.125rem;
    margin: 0px;
  }
}
.cell-content-line {
  border-bottom: 1px solid #efefef;
}
.calendar-disabled {
  background-color: #d1d3d4;
}
.closed-calendar {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 1.575rem;
  width: 42px;
  height: 42px;
}
</style>
